import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import { useCreateCoverSong, useUpdateCoverSong } from '@/hooks/use-music'
import type { Tables } from '@/types/supabase'

type CoverSong = Tables<'cover_songs'>

interface FormState {
  title: string
  original_artist: string
  notes: string
}

const EMPTY_FORM: FormState = {
  title: '',
  original_artist: '',
  notes: '',
}

interface CoverSongFormModalProps {
  open: boolean
  onClose: () => void
  song?: CoverSong
}

export default function CoverSongFormModal({ open, onClose, song }: CoverSongFormModalProps) {
  const isEdit = !!song
  const createCoverSong = useCreateCoverSong()
  const updateCoverSong = useUpdateCoverSong()

  const [form, setForm] = useState<FormState>(EMPTY_FORM)

  useEffect(() => {
    if (open) {
      if (song) {
        setForm({
          title: song.title,
          original_artist: song.original_artist,
          notes: song.notes ?? '',
        })
      } else {
        setForm(EMPTY_FORM)
      }
      createCoverSong.reset()
      updateCoverSong.reset()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, song?.id])

  const isPending = createCoverSong.isPending || updateCoverSong.isPending
  const mutationError = createCoverSong.error ?? updateCoverSong.error

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    const payload = {
      title: form.title.trim(),
      original_artist: form.original_artist.trim(),
      notes: form.notes.trim() || null,
    }

    if (isEdit && song) {
      updateCoverSong.mutate({ id: song.id, ...payload }, { onSuccess: onClose })
    } else {
      createCoverSong.mutate(payload, { onSuccess: onClose })
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="relative z-10 w-full max-w-md rounded-xl border border-border bg-card shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="font-semibold">{isEdit ? 'Edit Lagu Bawaan' : 'Tambah Lagu Bawaan'}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {/* Title */}
          <div className="space-y-1">
            <label className="text-sm font-medium">Judul Lagu *</label>
            <input
              required
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
              placeholder="Judul lagu"
            />
          </div>

          {/* Original artist */}
          <div className="space-y-1">
            <label className="text-sm font-medium">Artis / Grup Asli *</label>
            <input
              required
              value={form.original_artist}
              onChange={(e) => set('original_artist', e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
              placeholder="Nama artis atau grup pemilik lagu"
            />
          </div>

          {/* Notes */}
          <div className="space-y-1">
            <label className="text-sm font-medium">Catatan</label>
            <textarea
              rows={3}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none resize-none"
              placeholder="Misal: versi akustik, sering dibawain di encore (opsional)"
            />
          </div>

          {/* Error */}
          {mutationError && (
            <p className="text-sm text-destructive">
              {(mutationError as Error).message ?? 'Terjadi kesalahan. Coba lagi.'}
            </p>
          )}

          {/* Footer actions */}
          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border px-4 py-2 text-sm hover:bg-accent"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {isPending ? 'Menyimpan...' : isEdit ? 'Simpan Perubahan' : 'Tambah Lagu'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
